import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelection } from '@/hooks/useSelection';
import BatchActionsBar from '@/components/BatchActionsBar';
import PatientForm from '@/components/PatientForm';
import PatientOrders from '@/components/PatientOrders';
import PaginationControls from '@/components/PaginationControls';
import { usePagination } from '@/hooks/usePagination';
import {
  useCreatePatient,
  usePatients,
  Patient,
  MedicalRecord,
  usePatientNotes,
  useAddPatientNote,
  usePatientPrescriptions,
  usePatientLabResults,
  usePatientAttachments,
  useCreatePrescription,
  useCreateLabResult,
  useUploadAttachment,
  usePatientVisits,
  usePatientRecords,
  useCreateVisit,
  useCreateMedicalRecord,
  useUpdateVisit,
  useUpdateMedicalRecord,
  useInventoryItems,
} from '@/services/api';
import { PatientFormData } from '@/types/forms';
import useMockDataToast from '@/hooks/useMockDataToast';
import { useAuth } from '@/state/AuthContext';
import { useToasts } from '@/state/ToastContext';
import { useSearchParams } from 'react-router-dom';

const careFlags = [
  { title: 'Pain scoring', owner: 'Dr. Ramos', due: '17:00 today', severity: 'High' },
  { title: 'Biopsy review', owner: 'Dr. Patel', due: 'Tomorrow 09:00', severity: 'Medium' },
  { title: 'Owner callback', owner: 'Front desk', due: 'Within 2 hrs', severity: 'High' },
];

const automationQueue = [
  { label: 'Vaccine booster reminders', status: '26 scheduled', channel: 'SMS + email' },
  { label: 'Post-op instructions', status: '11 sent', channel: 'Owner portal' },
  { label: 'Medication refill', status: '5 pending', channel: 'App notification' },
];

const noteCategories = ['Rounds', 'Nursing', 'Client', 'Pharmacy', 'General'];

const formatFileSize = (bytes?: number) => {
  if (!bytes) return '??';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDateTime = (value?: string | null) => {
  if (!value) return 'Not recorded';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value ?? 'Not recorded';
  return parsed.toLocaleString();
};

const Patients: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState(() => searchParams.get('q') ?? '');
  const [speciesFilter, setSpeciesFilter] = useState('all');
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [noteCategory, setNoteCategory] = useState<string>(noteCategories[0]);
  const [noteBody, setNoteBody] = useState('');
  const [noteError, setNoteError] = useState<string | null>(null);
  const [prescriptionDraft, setPrescriptionDraft] = useState({
    medication: '',
    dosage: '',
    frequency: '',
    duration: '',
    instructions: '',
    inventoryItemId: '',
    inventoryQuantity: '',
  });
  const [labDraft, setLabDraft] = useState({
    testName: '',
    status: 'Pending',
    findings: '',
    performedAt: '',
  });
  const [prescriptionError, setPrescriptionError] = useState<string | null>(null);
  const [labError, setLabError] = useState<string | null>(null);
  const [attachmentDraft, setAttachmentDraft] = useState<{
    filename: string;
    contentType?: string;
    contentBase64?: string;
  }>({
    filename: '',
  });
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const [visitDraft, setVisitDraft] = useState({
    vetId: '',
    visitDate: '',
    reason: '',
    disposition: '',
  });
  const [visitError, setVisitError] = useState<string | null>(null);
  const [recordDraft, setRecordDraft] = useState({
    visitId: '',
    diagnosis: '',
    assessment: '',
    treatmentPlan: '',
    vitals: '',
    labSummary: '',
    imagingSummary: '',
    notes: '',
  });
  const [recordError, setRecordError] = useState<string | null>(null);
  const [attachmentRecordId, setAttachmentRecordId] = useState('');
  const [selectedVisitId, setSelectedVisitId] = useState('');
  const [selectedRecordId, setSelectedRecordId] = useState('');
  const [editingVisitId, setEditingVisitId] = useState('');
  const [editingRecordId, setEditingRecordId] = useState('');
  const [attachmentFilter, setAttachmentFilter] = useState<'all' | 'linked' | 'unlinked'>('all');
  const [attachmentSearch, setAttachmentSearch] = useState('');

  const {
    data: patients = [],
    isLoading,
    isFallback,
    fallbackMessage,
  } = usePatients();
  useMockDataToast(isFallback, 'Live patient registry unreachable. Showing demo roster.', fallbackMessage);
  const createPatient = useCreatePatient();
  const [formError, setFormError] = useState<string | null>(null);
  const patientIdentifier = selectedPatient?.id?.toString();
  const {
    data: patientNotes = [],
    isFallback: notesFallback,
    fallbackMessage: notesFallbackMessage,
    isLoading: notesLoading,
  } = usePatientNotes(patientIdentifier);
  useMockDataToast(
    notesFallback,
    'Patient notes service unreachable. Showing cached timeline.',
    notesFallbackMessage,
  );
  const addPatientNote = useAddPatientNote(patientIdentifier);
  const noteSubmitting = addPatientNote.isPending;
  const {
    data: prescriptions = [],
    isFallback: prescriptionsFallback,
    fallbackMessage: prescriptionsFallbackMessage,
    isLoading: prescriptionsLoading,
    error: prescriptionsError,
  } = usePatientPrescriptions(patientIdentifier);
  useMockDataToast(
    prescriptionsFallback,
    'Prescription service unreachable. Showing cached orders.',
    prescriptionsFallbackMessage,
  );
  const {
    data: labResults = [],
    isFallback: labsFallback,
    fallbackMessage: labsFallbackMessage,
    isLoading: labsLoading,
    error: labsError,
  } = usePatientLabResults(patientIdentifier);
  useMockDataToast(
    labsFallback,
    'Lab service unreachable. Showing cached results.',
    labsFallbackMessage,
  );
  const {
    data: attachments = [],
    isFallback: attachmentsFallback,
    fallbackMessage: attachmentsFallbackMessage,
  } = usePatientAttachments(patientIdentifier);
  useMockDataToast(
    attachmentsFallback,
    'Attachment store unreachable. Showing cached files.',
    attachmentsFallbackMessage,
  );
  const createPrescription = useCreatePrescription(patientIdentifier);
  const createLabResult = useCreateLabResult(patientIdentifier);
  const uploadAttachment = useUploadAttachment(patientIdentifier);
  const {
    data: visits = [],
    isFallback: visitsFallback,
    fallbackMessage: visitsFallbackMessage,
  } = usePatientVisits(patientIdentifier);
  useMockDataToast(
    visitsFallback,
    'Visit log service unreachable. Showing cached rounds.',
    visitsFallbackMessage,
  );
  const {
    data: medicalRecords = [],
    isFallback: recordsFallback,
    fallbackMessage: recordsFallbackMessage,
  } = usePatientRecords(patientIdentifier);
  const updateVisit = useUpdateVisit(patientIdentifier);
  const updateMedicalRecord = useUpdateMedicalRecord(patientIdentifier);
  useMockDataToast(
    recordsFallback,
    'Medical record service unreachable. Showing cached EMR.',
    recordsFallbackMessage,
  );
  const createVisit = useCreateVisit(patientIdentifier);
  const createMedicalRecord = useCreateMedicalRecord(patientIdentifier);
  const recordLookup = useMemo(() => {
    const entries = new Map<string, MedicalRecord>();
    medicalRecords.forEach((record) => entries.set(record.id, record));
    return entries;
  }, [medicalRecords]);
  const visitOptions = useMemo(
    () =>
      visits.map((visit) => ({
        id: visit.id,
        label: `${formatDateTime(visit.visitDate ?? visit.createdAt)}${
          visit.reason ? ` ?P ${visit.reason}` : ''
        }`,
      })),
    [visits],
  );
  const selectedVisit = useMemo(
    () => visits.find((visit) => visit.id === selectedVisitId),
    [selectedVisitId, visits],
  );
  const selectedRecord = useMemo(
    () => medicalRecords.find((record) => record.id === selectedRecordId),
    [selectedRecordId, medicalRecords],
  );
  const filteredAttachments = useMemo(() => {
    if (attachmentFilter === 'linked') return attachments.filter((file) => !!file.recordId);
    if (attachmentFilter === 'unlinked') return attachments.filter((file) => !file.recordId);
    return attachments;
  }, [attachments, attachmentFilter]);

  const attachmentSearchResults = useMemo(() => {
    const trimmed = attachmentSearch.trim().toLowerCase();
    if (!trimmed) return filteredAttachments;
    return filteredAttachments.filter((file) => {
      const haystack = `${file.filename ?? ''} ${file.contentType ?? ''} ${file.recordId ?? ''}`.toLowerCase();
      return haystack.includes(trimmed);
    });
  }, [filteredAttachments, attachmentSearch]);
  const { pushToast } = useToasts();
  const { hasPermission } = useAuth();
  const canWriteNotes = hasPermission('notes:write');
  const canConsumeInventory = hasPermission('inventory:consume');
  const { data: inventoryItems = [] } = useInventoryItems();
  const updateSearchParams = useCallback(
    (updates: Record<string, string | null | undefined>) => {
      const next = new URLSearchParams(searchParams);
      let changed = false;
      (Object.entries(updates) as [string, string | null | undefined][]).forEach(([key, value]) => {
        if (value === undefined || value === null || value === '') {
          if (next.has(key)) {
            next.delete(key);
            changed = true;
          }
          return;
        }
        if (next.get(key) !== value) {
          next.set(key, value);
          changed = true;
        }
      });
      if (changed) {
        setSearchParams(next, { replace: true });
      }
    },
    [searchParams, setSearchParams],
  );

  const selectPatient = useCallback(
    (patient: Patient | null, options?: { updateUrl?: boolean }) => {
      setSelectedPatient(patient);
      if (options?.updateUrl === false) return;
      updateSearchParams({ selected: patient ? String(patient.id) : null });
    },
    [updateSearchParams],
  );

  const handleQueryChange = (value: string) => {
    setQuery(value);
    updateSearchParams({ q: value });
  };

  const filteredPatients = useMemo(() => {
    return patients.filter((patient) => {
      const searchLower = query.toLowerCase();
      const matchesQuery =
        query.length === 0 ||
        patient.name.toLowerCase().includes(searchLower) ||
        patient.owner?.toLowerCase().includes(searchLower) ||
        patient.species.toLowerCase().includes(searchLower);
      
      const matchesSpecies =
        speciesFilter === 'all' || patient.species.toLowerCase() === speciesFilter;
      
      return matchesQuery && matchesSpecies;
    });
  }, [patients, query, speciesFilter]);

  useEffect(() => {
    const paramQuery = searchParams.get('q') ?? '';
    if (paramQuery !== query) {
      setQuery(paramQuery);
    }
  }, [searchParams, query]);

  useEffect(() => {
    const selectedParam = searchParams.get('selected');
    if (selectedParam) {
      const match = patients.find((patient) => String(patient.id) === selectedParam);
      if (match && match.id !== selectedPatient?.id) {
        selectPatient(match, { updateUrl: false });
        return;
      }
    }

    if (!selectedParam) {
      if (
        selectedPatient &&
        !filteredPatients.some((patient) => patient.id === selectedPatient.id)
      ) {
        selectPatient(filteredPatients[0] ?? null, { updateUrl: false });
        return;
      }
      if (!selectedPatient && filteredPatients.length > 0) {
        selectPatient(filteredPatients[0], { updateUrl: false });
      }
    }
  }, [searchParams, patients, filteredPatients, selectedPatient, selectPatient]);

  // Sorting
  const [patientSort, setPatientSort] = useState<{ key: 'name' | 'species' | 'age'; direction: 'asc' | 'desc' } | null>(null);
  const sortedPatients = useMemo(() => {
    if (!patientSort) return filteredPatients;
    const { key, direction } = patientSort;
    const sorted = [...filteredPatients].sort((a, b) => {
      let aVal: string | number = a[key] as any;
      let bVal: string | number = b[key] as any;
      if (typeof aVal === 'string') aVal = aVal.toLowerCase();
      if (typeof bVal === 'string') bVal = bVal.toLowerCase();
      if (aVal < bVal) return direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return direction === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [filteredPatients, patientSort]);

  const togglePatientSort = (key: 'name' | 'species' | 'age') => {
    setPatientSort((prev) => {
      if (!prev || prev.key !== key) return { key, direction: 'asc' };
      if (prev.direction === 'asc') return { key, direction: 'desc' };
      return null; // third click clears sort
    });
  };

  // Pagination
  const patientPage = usePagination(sortedPatients, { perPage: 25 });
  // Selection (batch operations)
  const patientSelection = useSelection<Patient['id']>();
  const allVisibleIds = patientPage.pageItems.map(p => p.id);
  const allVisibleSelected = patientSelection.allSelected(allVisibleIds) && allVisibleIds.length > 0;
  const toggleSelectAllVisible = () => {
    if (allVisibleSelected) {
      allVisibleIds.forEach(id => patientSelection.toggle(id));
    } else {
      patientSelection.selectMany(allVisibleIds);
    }
  };

  const handleCreatePatient = async (formData: PatientFormData) => {
    setFormError(null);
    try {
      const savedPatient = await createPatient.mutateAsync(formData);
      selectPatient(savedPatient);
      setShowModal(false);
    } catch {
      setFormError('Unable to save patient. Please try again.');
    }
  };

  const handleAddNote = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setNoteError(null);
    if (!selectedPatient) return;
    if (!canWriteNotes) {
      setNoteError('Your role cannot add clinical notes. Ask a medical lead to escalate access.');
      return;
    }
    if (noteBody.trim().length === 0) {
      setNoteError('請輸入臨床筆記內容後再儲存。');
      return;
    }
    try {
      await addPatientNote.mutateAsync({
        note: noteBody.trim(),
        category: noteCategory,
        author: 'Care Team',
        role: 'Staff',
      });
      setNoteBody('');
      setNoteError(null);
    } catch (error) {
      console.error('Failed to add patient note', error);
      setNoteError('Unable to save note right now.');
    }
  };

  const fileToBase64 = (file: File) =>
    new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result?.toString() ?? '';
        const [, base64] = result.split(',');
        resolve(base64 ?? result);
      };
      reader.onerror = () => reject(reader.error ?? new Error('Unable to read file'));
      reader.readAsDataURL(file);
    });

  const handleCreatePrescription = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setPrescriptionError(null);
    if (!selectedPatient || !prescriptionDraft.medication.trim()) {
      const message = 'Medication name is required';
      setPrescriptionError(message);
      pushToast({ tone: 'warning', message });
      return;
    }
    try {
      await createPrescription.mutateAsync({
        medication: prescriptionDraft.medication.trim(),
        dosage: prescriptionDraft.dosage,
        frequency: prescriptionDraft.frequency,
        duration: prescriptionDraft.duration,
        instructions: prescriptionDraft.instructions,
        inventoryItemId: canConsumeInventory && prescriptionDraft.inventoryItemId ? prescriptionDraft.inventoryItemId : undefined,
        inventoryQuantity:
          canConsumeInventory && prescriptionDraft.inventoryQuantity
            ? Number(prescriptionDraft.inventoryQuantity)
            : undefined,
      });
      setPrescriptionDraft({
        medication: '',
        dosage: '',
        frequency: '',
        duration: '',
        instructions: '',
        inventoryItemId: '',
        inventoryQuantity: '',
      });
      pushToast({ tone: 'success', message: 'Prescription added to chart' });
    } catch (error) {
      pushToast({
        tone: 'error',
        message: 'Unable to add prescription',
        detail: error instanceof Error ? error.message : undefined,
      });
      setPrescriptionError(error instanceof Error ? error.message : 'Unable to add prescription');
    }
  };

  const handleCreateLabResult = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLabError(null);
    if (!selectedPatient || !labDraft.testName.trim()) {
      const message = 'Test name is required';
      setLabError(message);
      pushToast({ tone: 'warning', message });
      return;
    }
    try {
      await createLabResult.mutateAsync({
        testName: labDraft.testName.trim(),
        status: labDraft.status,
        findings: labDraft.findings,
        performedAt: labDraft.performedAt || null,
      });
      setLabDraft({ testName: '', status: 'Pending', findings: '', performedAt: '' });
      pushToast({ tone: 'success', message: 'Lab result added' });
    } catch (error) {
      pushToast({
        tone: 'error',
        message: 'Unable to add lab result',
        detail: error instanceof Error ? error.message : undefined,
      });
      setLabError(error instanceof Error ? error.message : 'Unable to add lab result');
    }
  };
  const handleCreateVisit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selectedPatient) return;
    if (!visitDraft.reason.trim() && !visitDraft.disposition.trim()) {
      setVisitError('Add a reason or a disposition note');
      return;
    }
    try {
      if (editingVisitId) {
        await updateVisit.mutateAsync({
          visitId: editingVisitId,
          vetId: visitDraft.vetId || undefined,
          visitDate: visitDraft.visitDate || undefined,
          reason: visitDraft.reason || undefined,
          disposition: visitDraft.disposition || undefined,
        });
      } else {
        await createVisit.mutateAsync({
          vetId: visitDraft.vetId || undefined,
          visitDate: visitDraft.visitDate || undefined,
          reason: visitDraft.reason || undefined,
          disposition: visitDraft.disposition || undefined,
        });
      }
      setVisitDraft({ vetId: '', visitDate: '', reason: '', disposition: '' });
      setEditingVisitId('');
      setVisitError(null);
      pushToast({ tone: 'success', message: editingVisitId ? 'Visit updated' : 'Visit logged' });
    } catch (error) {
      setVisitError('Unable to log visit');
      pushToast({
        tone: 'error',
        message: editingVisitId ? 'Unable to update visit' : 'Unable to log visit',
        detail: error instanceof Error ? error.message : undefined,
      });
    }
  };

  const handleCreateMedicalRecord = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selectedPatient) return;
    if (
      !recordDraft.diagnosis.trim() &&
      !recordDraft.assessment.trim() &&
      !recordDraft.treatmentPlan.trim() &&
      !recordDraft.notes.trim()
    ) {
      setRecordError('Add clinical details before saving');
      return;
    }
    try {
      if (editingRecordId) {
        await updateMedicalRecord.mutateAsync({
          recordId: editingRecordId,
          visitId: recordDraft.visitId || undefined,
          diagnosis: recordDraft.diagnosis || undefined,
          assessment: recordDraft.assessment || undefined,
          treatmentPlan: recordDraft.treatmentPlan || undefined,
          vitals: recordDraft.vitals || undefined,
          labSummary: recordDraft.labSummary || undefined,
          imagingSummary: recordDraft.imagingSummary || undefined,
          notes: recordDraft.notes || undefined,
        });
      } else {
        await createMedicalRecord.mutateAsync({
          visitId: recordDraft.visitId || undefined,
          diagnosis: recordDraft.diagnosis || undefined,
          assessment: recordDraft.assessment || undefined,
          treatmentPlan: recordDraft.treatmentPlan || undefined,
          vitals: recordDraft.vitals || undefined,
          labSummary: recordDraft.labSummary || undefined,
          imagingSummary: recordDraft.imagingSummary || undefined,
          notes: recordDraft.notes || undefined,
        });
      }
      setRecordDraft({
        visitId: '',
        diagnosis: '',
        assessment: '',
        treatmentPlan: '',
        vitals: '',
        labSummary: '',
        imagingSummary: '',
        notes: '',
      });
      setEditingRecordId('');
      setRecordError(null);
      pushToast({ tone: 'success', message: editingRecordId ? 'Medical record updated' : 'Medical record saved' });
    } catch (error) {
      setRecordError(editingRecordId ? 'Unable to update record' : 'Unable to save record');
      pushToast({
        tone: 'error',
        message: editingRecordId ? 'Unable to update record' : 'Unable to save record',
        detail: error instanceof Error ? error.message : undefined,
      });
    }
  };
  const handleAttachmentFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const base64 = await fileToBase64(file);
      setAttachmentDraft({
        filename: file.name,
        contentType: file.type || 'application/octet-stream',
        contentBase64: base64,
      });
      setAttachmentError(null);
    } catch (error) {
      setAttachmentError('Unable to read file');
    }
  };

  const handleUploadAttachment = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selectedPatient) return;
    if (!attachmentDraft.filename || !attachmentDraft.contentBase64) {
      setAttachmentError('Select a file to upload');
      return;
    }
    try {
      await uploadAttachment.mutateAsync({
        filename: attachmentDraft.filename,
        contentType: attachmentDraft.contentType,
        contentBase64: attachmentDraft.contentBase64,
        recordId: attachmentRecordId || undefined,
      });
      setAttachmentDraft({ filename: '' });
      setAttachmentRecordId('');
      setAttachmentError(null);
      pushToast({ tone: 'success', message: 'Attachment uploaded' });
    } catch (error) {
      setAttachmentError('Unable to upload attachment');
      pushToast({
        tone: 'error',
        message: 'Unable to upload attachment',
        detail: error instanceof Error ? error.message : undefined,
      });
    }
  };

  const startEditVisit = (visitId: string) => {
    const visit = visits.find((v) => v.id === visitId);
    if (!visit) return;
    setEditingVisitId(visit.id);
    setSelectedVisitId(visit.id);
    setVisitDraft({
      vetId: visit.vetId?.toString() ?? '',
      visitDate: visit.visitDate ?? '',
      reason: visit.reason ?? '',
      disposition: visit.disposition ?? '',
    });
  };

  const startEditRecord = (recordId: string) => {
    const record = medicalRecords.find((r) => r.id === recordId);
    if (!record) return;
    setEditingRecordId(record.id);
    setSelectedRecordId(record.id);
    setRecordDraft({
      visitId: record.visitId ?? '',
      diagnosis: record.diagnosis ?? '',
      assessment: record.assessment ?? '',
      treatmentPlan: record.treatmentPlan ?? '',
      vitals: record.vitals ?? '',
      labSummary: record.labSummary ?? '',
      imagingSummary: record.imagingSummary ?? '',
      notes: record.notes ?? '',
    });
  };

  const resetVisitDraft = () => {
    setVisitDraft({ vetId: '', visitDate: '', reason: '', disposition: '' });
    setEditingVisitId('');
  };

  const resetRecordDraft = () => {
    setRecordDraft({
      visitId: '',
      diagnosis: '',
      assessment: '',
      treatmentPlan: '',
      vitals: '',
      labSummary: '',
      imagingSummary: '',
      notes: '',
    });
    setEditingRecordId('');
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <p className="section-title mb-1">Patient registry</p>
          <h1 className="text-2xl font-semibold text-slate-900">Patients</h1>
          <p className="text-sm text-slate-500">
            Track medical history, treatment plans, and caregiver communication.
          </p>
        </div>
        <div className="flex flex-1 flex-col gap-3 md:flex-row md:flex-none">
          <input
            type="search"
            placeholder="Search by patient or owner"
            value={query}
            onChange={(event) => handleQueryChange(event.target.value)}
            className="flex-1 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm focus:border-brand-blue focus:ring-brand-blue"
          />
          <div className="flex gap-2">
            {['all', 'canine', 'feline', 'exotic'].map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => setSpeciesFilter(option)}
                className={`rounded-xl px-3 py-2 text-xs font-semibold capitalize ${
                  speciesFilter === option
                    ? 'bg-brand-blue text-white'
                    : 'bg-white text-slate-500 shadow-sm'
                }`}
              >
                {option}
              </button>
            ))}
          </div>
          <button
            type="button"
            className="rounded-xl bg-brand-blue px-5 py-2 text-sm font-semibold text-white shadow-soft"
            onClick={() => setShowModal(true)}
          >
            + New patient
          </button>
        </div>
      </div>

      <div className="glass-panel overflow-hidden">
        {/* Batch actions bar */}
        <BatchActionsBar
          count={patientSelection.count}
          onClear={patientSelection.clear}
          scopeLabel="病患"
          actions={[
            {
              label: '匯出 CSV',
              variant: 'primary',
              onClick: () => {
                const selected = patients.filter(p => patientSelection.isSelected(p.id));
                const header = ['id','name','species','age'];
                const rows = selected.map(p => [p.id, p.name, p.species, String(p.age)]);
                const csv = [header.join(','), ...rows.map(r => r.join(','))].join('\n');
                const blob = new Blob([csv], { type: 'text/csv' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `patients-${selected.length}.csv`;
                a.click();
                URL.revokeObjectURL(url);
                pushToast({ tone: 'success', message: `已匯出 ${selected.length} 筆病患資料` });
              }
            },
            {
              label: '指派獸醫',
              onClick: () => {
                pushToast({ tone: 'info', message: '指派獸醫功能即將推出' });
              }
            },
            {
              label: '刪除',
              variant: 'danger',
              onClick: () => {
                const toDelete = patients.filter(p => patientSelection.isSelected(p.id));
                if (toDelete.length === 0) return;
                if (!window.confirm(`確定要刪除 ${toDelete.length} 位病患? 此操作無法復原。`)) return;
                // placeholder for delete API integration
                pushToast({ tone: 'success', message: `已刪除 ${toDelete.length} 位病患 (示範)` });
                patientSelection.clear();
              }
            }
          ]}
        />
        <div className="overflow-x-auto">
          <table className="w-full min-w-[600px] table-auto divide-y divide-slate-100 text-sm">
            <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-3 py-3">
                  <input
                    type="checkbox"
                    aria-label="Select all visible patients"
                    checked={allVisibleSelected}
                    onChange={toggleSelectAllVisible}
                    className="h-4 w-4 rounded border-slate-300 text-brand-blue focus:ring-brand-blue"
                  />
                </th>
                <th className="px-6 py-3">
                  <button
                    type="button"
                    onClick={() => togglePatientSort('name')}
                    className="flex items-center gap-1 font-semibold transition hover:text-slate-700"
                  >
                    Patient
                    {patientSort?.key === 'name' && (
                      <span className="text-[10px] font-normal">
                        {patientSort.direction === 'asc' ? '▲' : '▼'}
                      </span>
                    )}
                  </button>
                </th>
                <th className="px-6 py-3">
                  <button
                    type="button"
                    onClick={() => togglePatientSort('species')}
                    className="flex items-center gap-1 font-semibold transition hover:text-slate-700"
                  >
                    Species
                    {patientSort?.key === 'species' && (
                      <span className="text-[10px] font-normal">
                        {patientSort.direction === 'asc' ? '▲' : '▼'}
                      </span>
                    )}
                  </button>
                </th>
                <th className="px-6 py-3">
                  <button
                    type="button"
                    onClick={() => togglePatientSort('age')}
                    className="flex items-center gap-1 font-semibold transition hover:text-slate-700"
                  >
                    Age
                    {patientSort?.key === 'age' && (
                      <span className="text-[10px] font-normal">
                        {patientSort.direction === 'asc' ? '▲' : '▼'}
                      </span>
                    )}
                  </button>
                </th>
                <th className="px-6 py-3">Status</th>
                <th className="px-6 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white text-slate-700">
              {isLoading ? (
                <tr>
                  <td colSpan={5} className="px-6 py-10 text-center text-slate-400">
                    Loading patient roster...
                  </td>
                </tr>
              ) : filteredPatients.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-10 text-center text-slate-400">
                    No patients found.
                  </td>
                </tr>
              ) : (
                patientPage.pageItems.map((patient) => (
                  <tr
                    key={patient.id}
                    className="cursor-pointer hover:bg-slate-50"
                    onClick={() => selectPatient(patient)}
                  >
                    <td className="px-3 py-4">
                      <input
                        type="checkbox"
                        aria-label={`Select patient ${patient.name}`}
                        checked={patientSelection.isSelected(patient.id)}
                        onChange={(e) => {
                          e.stopPropagation();
                          patientSelection.toggle(patient.id);
                        }}
                        className="h-4 w-4 rounded border-slate-300 text-brand-blue focus:ring-brand-blue"
                      />
                    </td>
                    <td className="px-6 py-4 font-medium text-slate-900">{patient.name}</td>
                    <td className="px-6 py-4 capitalize">{patient.species}</td>
                    <td className="px-6 py-4">{patient.age} yrs</td>
                    <td className="px-6 py-4">
                      <span className="pill bg-emerald-50 text-emerald-600">Active</span>
                    </td>
                    <td className="px-6 py-4 text-right text-xs text-brand-blue">
                      <Link
                        to={`/patients/${encodeURIComponent(String(patient.id))}`}
                        className="underline-offset-2 hover:underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        View chart
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <PaginationControls
          page={patientPage.page}
          perPage={patientPage.perPage}
          total={patientPage.total}
          totalPages={patientPage.totalPages}
          canNext={patientPage.canNext}
          canPrev={patientPage.canPrev}
          startIndex={patientPage.startIndex}
          endIndex={patientPage.endIndex}
          onPageChange={patientPage.setPage}
          onPerPageChange={patientPage.setPerPage}
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="glass-panel space-y-4 p-6 lg:col-span-2">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">
                {selectedPatient?.name ?? 'No patient selected'} · diagnostics
              </h3>
            <p className="mt-2 text-sm text-slate-500">
              Centralize escalations so triage, surgery, and client services stay aligned.
            </p>
          </div>
          <ul className="space-y-3 text-sm">
            {careFlags.map((flag) => (
              <li
                key={flag.title}
                className="flex items-center justify-between rounded-2xl border border-slate-100 bg-white px-4 py-3"
              >
                <div>
                  <p className="font-semibold text-slate-900">{flag.title}</p>
                  <p className="text-xs text-slate-500">{flag.owner}</p>
                </div>
                <span
                  className={`pill ${
                    flag.severity === 'High'
                      ? 'bg-rose-50 text-rose-600'
                      : 'bg-amber-50 text-amber-600'
                  }`}
                >
                  {flag.due}
                </span>
              </li>
            ))}
          </ul>
        </div>
        <div className="glass-panel space-y-4 p-6">
          {selectedPatient ? (
            <>
              <p className="section-title mb-1">Selected patient</p>
              <h3 className="text-xl font-semibold text-slate-900">{selectedPatient?.name ?? "No patient selected"}</h3>
              <p className="text-sm text-slate-500">
                {selectedPatient.species}, {selectedPatient.age} yrs
              </p>
              <dl className="mt-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <dt className="text-slate-500">Caregiver</dt>
                  <dd className="font-semibold">{selectedPatient.owner ?? 'N/A'}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-slate-500">Last visit</dt>
                  <dd className="font-semibold">{selectedPatient.lastVisit ?? 'Mar 3, 2025'}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-slate-500">Primary doctor</dt>
                  <dd className="font-semibold">{selectedPatient.doctor ?? 'Dr. Avery'}</dd>
                </div>
              </dl>
            </>
          ) : (
            <p className="text-sm text-slate-500">Select a patient to preview details.</p>
          )}
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="glass-panel space-y-3 p-6">
          <div>
            <p className="section-title mb-1">Automation</p>
            <h3 className="text-lg font-semibold text-slate-900">
                {selectedPatient?.name ?? 'No patient selected'} · diagnostics
              </h3>
          </div>
          <ul className="space-y-2 text-sm">
            {automationQueue.map((item) => (
              <li
                key={item.label}
                className="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3"
              >
                <p className="font-semibold text-slate-900">{item.label}</p>
                <p className="text-xs text-slate-500">{item.channel}</p>
                <p className="text-xs font-semibold text-brand-blue">{item.status}</p>
              </li>
            ))}
          </ul>
        </div>
        <div className="glass-panel space-y-4 p-6 lg:col-span-2">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="section-title mb-1">Clinical documentation</p>
              <h3 className="text-lg font-semibold text-slate-900">
                {selectedPatient?.name ?? "No patient selected"} · diagnostics
              </h3>
            </div>
            {selectedPatient && (
              <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-500">
                {patientNotes.length} entries
              </span>
            )}
          </div>

          {selectedPatient ? (
            <>
              {notesLoading ? (
                <p className="text-sm text-slate-500">Loading notes timeline...</p>
              ) : patientNotes.length === 0 ? (
                <p className="text-sm text-slate-500">
                  No documented notes yet. Capture the first update below.
                </p>
              ) : (
                <ul className="space-y-3 text-sm">
                  {patientNotes.map((note) => (
                    <li
                      key={note.id}
                      className="rounded-2xl border border-slate-100 bg-slate-50 p-4 text-slate-700"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <p className="text-sm font-semibold text-slate-900">{note.category}</p>
                          <p className="text-xs text-slate-500">
                            {note.author} ??{note.role}
                          </p>
                        </div>
                        <span className="text-[11px] text-slate-400">
                          {new Date(note.createdAt).toLocaleString()}
                        </span>
                      </div>
                      <p className="mt-2 text-slate-700">{note.note}</p>
                    </li>
                  ))}
                </ul>
              )}

              <form className="space-y-3 pt-2" onSubmit={handleAddNote} aria-busy={noteSubmitting}>
                <div className="grid gap-3 md:grid-cols-2">
                  <label className="text-xs font-semibold text-slate-500">
                    Category
                    <select
                      className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm focus:border-brand-blue focus:ring-brand-blue disabled:bg-slate-100"
                      value={noteCategory}
                      onChange={(event) => setNoteCategory(event.target.value)}
                      disabled={!canWriteNotes || noteSubmitting}
                    >
                      {noteCategories.map((category) => (
                        <option key={category} value={category}>
                          {category}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="text-xs font-semibold text-slate-500">
                    Visibility
                    <input
                      disabled
                      className="mt-1 w-full rounded-xl border border-slate-200 bg-slate-100 px-3 py-2 text-sm text-slate-500"
                      value="Internal care team"
                    />
                  </label>
                </div>
                <textarea
                  className="min-h-[140px] w-full rounded-2xl border border-slate-200 bg-white p-4 text-sm text-slate-700 focus:border-brand-blue focus:ring-brand-blue disabled:bg-slate-100"
                  placeholder="Document SOAP updates, client communication, or shift handoffs."
                  value={noteBody}
                  onChange={(event) => setNoteBody(event.target.value)}
                  disabled={!canWriteNotes || noteSubmitting}
                  maxLength={1000}
                />
                <p className="text-right text-[11px] text-slate-400">
                  {noteBody.length}/1000
                </p>
                {!canWriteNotes && (
                  <p className="rounded-xl bg-amber-50 px-4 py-2 text-xs font-semibold text-amber-800">
                    Your role can review notes but needs medical leadership access to author updates.
                  </p>
                )}
                {noteError && (
                  <p className="text-xs font-semibold text-rose-600">
                    {noteError}
                  </p>
                )}
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    className="rounded-xl border border-slate-200 px-4 py-2 text-xs font-semibold text-slate-600 disabled:opacity-50"
                    onClick={() => setNoteBody('')}
                    disabled={!canWriteNotes || noteSubmitting}
                  >
                    Clear
                  </button>
                  <button
                    type="submit"
                    disabled={
                      !canWriteNotes || noteSubmitting || noteBody.trim().length === 0
                    }
                    className="rounded-xl bg-brand-blue px-4 py-2 text-xs font-semibold text-white shadow-soft disabled:opacity-50"
                  >
                    {noteSubmitting ? 'Saving…' : 'Add note'}
                  </button>
                </div>
                {noteSubmitting && (
                  <p className="text-right text-xs text-slate-500" role="status">
                    正在儲存筆記…
                  </p>
                )}
              </form>
            </>
          ) : (
            <p className="text-sm text-slate-500">Select a patient to review documentation.</p>
          )}
        </div>
      </div>

      {/* Care Orders Section */}
      {selectedPatient && (
        <div className="glass-panel p-6">
          <PatientOrders patientId={selectedPatient.id} />
        </div>
      )}

      {selectedPatient && (
        <div className="grid gap-4 lg:grid-cols-3">
          <div className="glass-panel space-y-3 p-6">
            <div>
              <p className="section-title mb-1">Medications</p>
              <h3 className="text-lg font-semibold text-slate-900">
                {selectedPatient?.name ?? "No patient selected"} · diagnostics
              </h3>
            </div>
            {prescriptionsLoading ? (
              <p className="text-sm text-slate-500">Loading prescriptions…</p>
            ) : prescriptionsError ? (
              <p className="text-sm text-rose-600">Unable to load prescriptions.</p>
            ) : prescriptions.length === 0 ? (
              <p className="text-sm text-slate-500">No prescriptions recorded.</p>
            ) : (
              <ul className="space-y-2 text-sm">
                {prescriptions.map((rx) => (
                  <li
                    key={rx.id}
                    className="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3 text-slate-700"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-semibold text-slate-900">{rx.medication}</p>
                      <span className="text-[11px] text-slate-400">
                        {new Date(rx.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500">
                      {rx.dosage} {rx.frequency ? `??${rx.frequency}` : ''}
                    </p>
                    {rx.instructions && (
                      <p className="mt-1 text-xs text-slate-500">{rx.instructions}</p>
                    )}
                  </li>
                ))}
              </ul>
            )}
            {prescriptionsFallback && (
              <p className="text-[11px] text-amber-600">
                {prescriptionsFallbackMessage ?? 'Prescription service unreachable. Showing cached orders.'}
              </p>
            )}
            <form className="space-y-2 pt-2" onSubmit={handleCreatePrescription}>
              <input
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                placeholder="Medication"
                value={prescriptionDraft.medication}
                onChange={(event) =>
                  setPrescriptionDraft((prev) => ({ ...prev, medication: event.target.value }))
                }
                disabled={!canWriteNotes}
              />
              <div className="grid gap-2 md:grid-cols-2">
                <input
                  className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
                  placeholder="Dosage"
                  value={prescriptionDraft.dosage}
                  onChange={(event) =>
                    setPrescriptionDraft((prev) => ({ ...prev, dosage: event.target.value }))
                  }
                  disabled={!canWriteNotes}
                />
                <input
                  className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
                  placeholder="Frequency"
                  value={prescriptionDraft.frequency}
                  onChange={(event) =>
                    setPrescriptionDraft((prev) => ({ ...prev, frequency: event.target.value }))
                  }
                  disabled={!canWriteNotes}
                />
              </div>
              <input
                className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
                placeholder="Duration"
                value={prescriptionDraft.duration}
                onChange={(event) =>
                  setPrescriptionDraft((prev) => ({ ...prev, duration: event.target.value }))
                }
                disabled={!canWriteNotes}
              />
              <textarea
                className="min-h-[80px] rounded-xl border border-slate-200 px-3 py-2 text-sm"
                placeholder="Instructions"
                value={prescriptionDraft.instructions}
                onChange={(event) =>
                  setPrescriptionDraft((prev) => ({ ...prev, instructions: event.target.value }))
                }
                disabled={!canWriteNotes}
              />
              <div className="grid gap-2 md:grid-cols-2">
                <select
                  className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
                  value={prescriptionDraft.inventoryItemId}
                  onChange={(event) =>
                    setPrescriptionDraft((prev) => ({ ...prev, inventoryItemId: event.target.value }))
                  }
                  disabled={!canConsumeInventory}
                >
                  <option value="">Link inventory item (optional)</option>
                  {inventoryItems.map((item: any) => (
                    <option key={item.id} value={item.id}>
                      {item.name} {item.sku ? `(${item.sku})` : ''} · {item.quantityOnHand ?? 0}{' '}
                      {item.unit ?? ''}
                    </option>
                  ))}
                </select>
                <input
                  className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
                  type="number"
                  min="0"
                  step="1"
                  placeholder="Quantity to deduct"
                  value={prescriptionDraft.inventoryQuantity}
                  onChange={(event) =>
                    setPrescriptionDraft((prev) => ({
                      ...prev,
                      inventoryQuantity: event.target.value,
                    }))
                  }
                  disabled={!canConsumeInventory}
                />
              </div>
              {!canConsumeInventory && (
                <p className="text-[11px] text-slate-400">
                  Inventory deduction requires permission.
                </p>
              )}
              <button
                type="submit"
                disabled={!canWriteNotes || createPrescription.isPending || !prescriptionDraft.medication}
                className="w-full rounded-xl bg-brand-blue px-4 py-2 text-xs font-semibold text-white shadow-soft disabled:cursor-not-allowed disabled:opacity-50"
              >
                {createPrescription.isPending ? 'Saving...' : 'Add prescription'}
              </button>
              {prescriptionError && (
                <p className="text-xs text-rose-600" role="alert">
                  {prescriptionError}
                </p>
              )}
            </form>
          </div>

          <div className="glass-panel space-y-3 p-6">
            <div>
              <p className="section-title mb-1">Visit log</p>
              <h3 className="text-lg font-semibold text-slate-900">
                {selectedPatient?.name ?? "No patient selected"} · diagnostics
              </h3>
            </div>
            {visits.length === 0 ? (
              <p className="text-sm text-slate-500">No visits recorded yet.</p>
            ) : (
              <ul className="space-y-2 text-sm">
                {visits.map((visit) => (
                  <li
                    key={visit.id}
                    className="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3 text-slate-700"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-semibold text-slate-900">
                        {visit.reason || 'Unspecified visit'}
                      </p>
                      <span className="text-[11px] text-slate-400">
                        {formatDateTime(visit.visitDate ?? visit.createdAt)}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500">
                      {visit.disposition ? visit.disposition : 'Disposition pending'}
                    </p>
                    <div className="mt-2 flex gap-2 text-[11px]">
                      <button
                        type="button"
                        className={`rounded-full px-3 py-1 font-semibold ${
                          selectedVisitId === visit.id
                            ? 'bg-slate-900 text-white'
                            : 'bg-slate-100 text-slate-700'
                        }`}
                        onClick={() => setSelectedVisitId(visit.id)}
                      >
                        View
                      </button>
                      <button
                        type="button"
                        className="rounded-full bg-white px-3 py-1 font-semibold text-brand-blue shadow-sm"
                        onClick={() => startEditVisit(visit.id)}
                        disabled={!canWriteNotes}
                      >
                        Edit
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
            {visitsFallback && (
              <p className="rounded-xl bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800">
                {visitsFallbackMessage ?? 'Visit API unreachable. Showing cached data.'}
              </p>
            )}
            {selectedVisit && (
              <div className="rounded-2xl border border-slate-100 bg-white px-4 py-3 text-xs text-slate-600">
                <p className="font-semibold text-slate-900">Selected visit</p>
                <p>Date: {formatDateTime(selectedVisit.visitDate ?? selectedVisit.createdAt)}</p>
                {selectedVisit.reason && <p>Reason: {selectedVisit.reason}</p>}
                {selectedVisit.disposition && <p>Disposition: {selectedVisit.disposition}</p>}
              </div>
            )}
            <form className="space-y-2 pt-2" onSubmit={handleCreateVisit}>
              <div className="grid gap-2 md:grid-cols-2">
                <input
                  className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
                  placeholder="Attending vet ID"
                  value={visitDraft.vetId}
                  onChange={(event) =>
                    setVisitDraft((prev) => ({ ...prev, vetId: event.target.value }))
                  }
                  disabled={!canWriteNotes}
                />
                <input
                  type="datetime-local"
                  className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
                  value={visitDraft.visitDate}
                  onChange={(event) =>
                    setVisitDraft((prev) => ({ ...prev, visitDate: event.target.value }))
                  }
                  disabled={!canWriteNotes}
                />
              </div>
              <input
                className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
                placeholder="Reason for visit"
                value={visitDraft.reason}
                onChange={(event) =>
                  setVisitDraft((prev) => ({ ...prev, reason: event.target.value }))
                }
                disabled={!canWriteNotes}
              />
              <textarea
                className="min-h-[80px] rounded-xl border border-slate-200 px-3 py-2 text-sm"
                placeholder="Disposition, next steps, or owner instructions"
                value={visitDraft.disposition}
                onChange={(event) =>
                  setVisitDraft((prev) => ({ ...prev, disposition: event.target.value }))
                }
                disabled={!canWriteNotes}
              />
              {visitError && <p className="text-xs font-semibold text-rose-600">{visitError}</p>}
              <button
                type="submit"
                disabled={!canWriteNotes || createVisit.isPending || updateVisit.isPending}
                className="w-full rounded-xl bg-emerald-600 px-4 py-2 text-xs font-semibold text-white shadow-soft disabled:cursor-not-allowed disabled:opacity-50"
              >
                {editingVisitId
                  ? updateVisit.isPending
                    ? 'Updating...'
                    : 'Update visit'
                  : createVisit.isPending
                  ? 'Logging...'
                  : 'Add visit'}
              </button>
              {editingVisitId && (
                <button
                  type="button"
                  className="w-full rounded-xl border border-slate-200 px-4 py-2 text-xs font-semibold text-slate-700"
                  onClick={resetVisitDraft}
                >
                  Cancel edit
                </button>
              )}
            </form>
          </div>

          <div className="glass-panel space-y-3 p-6">
            <div>
              <p className="section-title mb-1">Medical records</p>
              <h3 className="text-lg font-semibold text-slate-900">
                {selectedPatient?.name ?? "No patient selected"} · diagnostics
              </h3>
            </div>
            {medicalRecords.length === 0 ? (
              <p className="text-sm text-slate-500">No EMR entries yet.</p>
            ) : (
              <ul className="space-y-2 text-sm">
                {medicalRecords.map((record) => (
                  <li
                    key={record.id}
                    className="rounded-2xl border border-slate-100 bg-white px-4 py-3 text-slate-700"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-semibold text-slate-900">
                        {record.diagnosis || record.assessment || 'Clinical note'}
                      </p>
                      <span className="text-[11px] text-slate-400">
                        {formatDateTime(record.visitDate ?? record.createdAt)}
                      </span>
                    </div>
                    {record.treatmentPlan && (
                      <p className="text-xs text-slate-500">Plan: {record.treatmentPlan}</p>
                    )}
                    {record.notes && (
                      <p className="mt-1 text-xs text-slate-500">Notes: {record.notes}</p>
                    )}
                    <div className="mt-2 flex gap-2 text-[11px]">
                      <button
                        type="button"
                        className={`rounded-full px-3 py-1 font-semibold ${
                          selectedRecordId === record.id
                            ? 'bg-slate-900 text-white'
                            : 'bg-slate-100 text-slate-700'
                        }`}
                        onClick={() => setSelectedRecordId(record.id)}
                      >
                        View
                      </button>
                      <button
                        type="button"
                        className="rounded-full bg-white px-3 py-1 font-semibold text-brand-blue shadow-sm"
                        onClick={() => startEditRecord(record.id)}
                        disabled={!canWriteNotes}
                      >
                        Edit
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
            {recordsFallback && (
              <p className="rounded-xl bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800">
                {recordsFallbackMessage ?? 'EMR API unreachable. Showing cached data.'}
              </p>
            )}
            {selectedRecord && (
              <div className="rounded-2xl border border-slate-100 bg-white px-4 py-3 text-xs text-slate-600">
                <p className="font-semibold text-slate-900">Selected record</p>
                <p>Date: {formatDateTime(selectedRecord.visitDate ?? selectedRecord.createdAt)}</p>
                {selectedRecord.diagnosis && <p>Dx: {selectedRecord.diagnosis}</p>}
                {selectedRecord.treatmentPlan && <p>Plan: {selectedRecord.treatmentPlan}</p>}
                {selectedRecord.notes && <p>Notes: {selectedRecord.notes}</p>}
              </div>
            )}
            <form className="space-y-2 pt-2" onSubmit={handleCreateMedicalRecord}>
              <label className="text-xs font-semibold text-slate-500">
                Link to visit (optional)
                <select
                  className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                  value={recordDraft.visitId}
                  onChange={(event) =>
                    setRecordDraft((prev) => ({ ...prev, visitId: event.target.value }))
                  }
                  disabled={!canWriteNotes}
                >
                  <option value="">Standalone record</option>
                  {visitOptions.map((visit) => (
                    <option key={visit.id} value={visit.id}>
                      {visit.label}
                    </option>
                  ))}
                </select>
              </label>
              <div className="grid gap-2 md:grid-cols-2">
                <input
                  className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
                  placeholder="Diagnosis"
                  value={recordDraft.diagnosis}
                  onChange={(event) =>
                    setRecordDraft((prev) => ({ ...prev, diagnosis: event.target.value }))
                  }
                  disabled={!canWriteNotes}
                />
                <input
                  className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
                  placeholder="Assessment"
                  value={recordDraft.assessment}
                  onChange={(event) =>
                    setRecordDraft((prev) => ({ ...prev, assessment: event.target.value }))
                  }
                  disabled={!canWriteNotes}
                />
              </div>
              <textarea
                className="min-h-[80px] rounded-xl border border-slate-200 px-3 py-2 text-sm"
                placeholder="Treatment plan"
                value={recordDraft.treatmentPlan}
                onChange={(event) =>
                  setRecordDraft((prev) => ({ ...prev, treatmentPlan: event.target.value }))
                }
                disabled={!canWriteNotes}
              />
              <textarea
                className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
                placeholder="Vitals"
                value={recordDraft.vitals}
                onChange={(event) =>
                  setRecordDraft((prev) => ({ ...prev, vitals: event.target.value }))
                }
                disabled={!canWriteNotes}
              />
              <div className="grid gap-2 md:grid-cols-2">
                <textarea
                  className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
                  placeholder="Lab summary"
                  value={recordDraft.labSummary}
                  onChange={(event) =>
                    setRecordDraft((prev) => ({ ...prev, labSummary: event.target.value }))
                  }
                  disabled={!canWriteNotes}
                />
                <textarea
                  className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
                  placeholder="Imaging summary"
                  value={recordDraft.imagingSummary}
                  onChange={(event) =>
                    setRecordDraft((prev) => ({ ...prev, imagingSummary: event.target.value }))
                  }
                  disabled={!canWriteNotes}
                />
              </div>
              <textarea
                className="min-h-[80px] rounded-xl border border-slate-200 px-3 py-2 text-sm"
                placeholder="Care team notes / owner communication"
                value={recordDraft.notes}
                onChange={(event) =>
                  setRecordDraft((prev) => ({ ...prev, notes: event.target.value }))
                }
                disabled={!canWriteNotes}
              />
              {recordError && <p className="text-xs font-semibold text-rose-600">{recordError}</p>}
              <button
                type="submit"
                disabled={
                  !canWriteNotes || createMedicalRecord.isPending || updateMedicalRecord.isPending
                }
                className="w-full rounded-xl bg-brand-indigo px-4 py-2 text-xs font-semibold text-white shadow-soft disabled:cursor-not-allowed disabled:opacity-50"
              >
                {editingRecordId
                  ? updateMedicalRecord.isPending
                    ? 'Updating...'
                    : 'Update record'
                  : createMedicalRecord.isPending
                  ? 'Saving...'
                  : 'Save record'}
              </button>
              {editingRecordId && (
                <button
                  type="button"
                  className="w-full rounded-xl border border-slate-200 px-4 py-2 text-xs font-semibold text-slate-700"
                  onClick={resetRecordDraft}
                >
                  Cancel edit
                </button>
              )}
            </form>
          </div>

          <div className="glass-panel space-y-3 p-6">
            <div>
              <p className="section-title mb-1">Lab results</p>
              <h3 className="text-lg font-semibold text-slate-900">
                {selectedPatient?.name ?? "No patient selected"} · diagnostics
              </h3>
            </div>
            {labsLoading ? (
              <p className="text-sm text-slate-500">Loading lab work…</p>
            ) : labsError ? (
              <p className="text-sm text-rose-600">Unable to load lab feed.</p>
            ) : labResults.length === 0 ? (
              <p className="text-sm text-slate-500">No lab work recorded.</p>
            ) : (
              <ul className="space-y-2 text-sm">
                {labResults.map((lab) => (
                  <li
                    key={lab.id}
                    className="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3 text-slate-700"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-semibold text-slate-900">{lab.testName}</p>
                      <span className="text-[11px] text-slate-400">
                        {new Date(lab.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500">Status: {lab.status}</p>
                    {lab.findings && (
                      <p className="mt-1 text-xs text-slate-500">{lab.findings}</p>
                    )}
                  </li>
                ))}
              </ul>
            )}
            {labsFallback && (
              <p className="text-[11px] text-amber-600">
                {labsFallbackMessage ?? 'Lab service unreachable. Showing cached findings.'}
              </p>
            )}
            <form className="space-y-2 pt-2" onSubmit={handleCreateLabResult}>
              <input
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                placeholder="Test name"
                value={labDraft.testName}
                onChange={(event) =>
                  setLabDraft((prev) => ({ ...prev, testName: event.target.value }))
                }
                disabled={!canWriteNotes}
              />
              <input
                className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
                placeholder="Findings / summary"
                value={labDraft.findings}
                onChange={(event) =>
                  setLabDraft((prev) => ({ ...prev, findings: event.target.value }))
                }
                disabled={!canWriteNotes}
              />
              <div className="grid gap-2 md:grid-cols-2">
                <select
                  className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
                  value={labDraft.status}
                  onChange={(event) =>
                    setLabDraft((prev) => ({ ...prev, status: event.target.value }))
                  }
                  disabled={!canWriteNotes}
                >
                  {['Pending', 'In progress', 'Completed'].map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
                <input
                  type="datetime-local"
                  className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
                  value={labDraft.performedAt}
                  onChange={(event) =>
                    setLabDraft((prev) => ({ ...prev, performedAt: event.target.value }))
                  }
                  disabled={!canWriteNotes}
                />
              </div>
              <button
                type="submit"
                disabled={!canWriteNotes || createLabResult.isPending || !labDraft.testName}
                className="w-full rounded-xl bg-brand-indigo px-4 py-2 text-xs font-semibold text-white shadow-soft disabled:cursor-not-allowed disabled:opacity-50"
              >
                {createLabResult.isPending ? 'Saving...' : 'Add lab result'}
              </button>
              {labError && (
                <p className="text-xs text-rose-600" role="alert">
                  {labError}
                </p>
              )}
            </form>
          </div>

          <div className="glass-panel space-y-3 p-6">
            <div>
              <p className="section-title mb-1">Attachments</p>
              <h3 className="text-lg font-semibold text-slate-900">
                {selectedPatient?.name ?? "No patient selected"} · files
              </h3>
            </div>
            <div className="flex flex-wrap items-center gap-2 text-xs">
              {['all', 'linked', 'unlinked'].map((opt) => (
                <button
                  key={opt}
                  type="button"
                  className={`rounded-full border px-3 py-1 font-semibold ${
                    attachmentFilter === opt
                      ? 'border-brand-blue bg-brand-blue/10 text-brand-blue'
                      : 'border-slate-200 bg-white text-slate-500'
                  }`}
                  onClick={() => setAttachmentFilter(opt as typeof attachmentFilter)}
                >
                  {opt === 'all' ? 'All' : opt === 'linked' ? 'Linked to EMR' : 'Unlinked'}
                </button>
              ))}
              <input
                type="search"
                value={attachmentSearch}
                onChange={(event) => setAttachmentSearch(event.target.value)}
                placeholder="Search filename or type"
                className="rounded-full border border-slate-200 px-3 py-1 text-xs text-slate-600 focus:border-brand-blue focus:ring-brand-blue/20"
              />
            </div>
            {attachmentSearchResults.length === 0 ? (
              <p className="text-sm text-slate-500">No files uploaded yet.</p>
            ) : (
              <ul className="space-y-2 text-sm">
                {attachmentSearchResults.map((file) => {
                  const href =
                    file.contentBase64 && file.contentType
                      ? `data:${file.contentType};base64,${file.contentBase64}`
                      : undefined;
                  const linkedRecord = file.recordId ? recordLookup.get(file.recordId) : undefined;
                  return (
                    <li
                      key={file.id}
                      className="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3 text-slate-700"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <p className="font-semibold text-slate-900">{file.filename}</p>
                        <span className="text-[11px] text-slate-400">
                          {formatFileSize(file.sizeBytes)}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500">{file.contentType}</p>
                      {file.recordId && (
                        <p className="text-xs text-slate-500">
                          Linked to{' '}
                          {linkedRecord?.diagnosis ||
                            linkedRecord?.notes ||
                            `record ${file.recordId}`}
                        </p>
                      )}
                      {href && (
                        <a
                          href={href}
                          download={file.filename}
                          className="text-xs font-semibold text-brand-blue"
                        >
                          Download
                        </a>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}<form className="space-y-2 pt-2" onSubmit={handleUploadAttachment}>
              <label className="text-xs font-semibold text-slate-500">
                Link to EMR entry (optional)
                <select
                  className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                  value={attachmentRecordId}
                  onChange={(event) => setAttachmentRecordId(event.target.value)}
                  disabled={!canWriteNotes || medicalRecords.length === 0}
                >
                  <option value="">No EMR link</option>
                  {medicalRecords.map((record) => (
                    <option key={record.id} value={record.id}>
                      {record.diagnosis || record.notes || record.id}
                    </option>
                  ))}
                </select>
              </label>
              <input
                type="file"
                className="w-full text-xs text-slate-500 file:mr-3 file:rounded-full file:border-0 file:bg-brand-blue/10 file:px-3 file:py-1 file:text-xs file:font-semibold file:text-brand-blue disabled:opacity-50"
                onChange={handleAttachmentFile}
                disabled={!canWriteNotes}
              />
              {attachmentDraft.filename && (
                <p className="text-xs text-slate-500">
                  Ready to upload: {attachmentDraft.filename}
                </p>
              )}
              {attachmentError && (
                <p className="text-xs font-semibold text-rose-600">{attachmentError}</p>
              )}
              <button
                type="submit"
                disabled={
                  !canWriteNotes ||
                  uploadAttachment.isPending ||
                  !attachmentDraft.filename ||
                  !attachmentDraft.contentBase64
                }
                className="w-full rounded-xl bg-slate-900 px-4 py-2 text-xs font-semibold text-white shadow-soft disabled:cursor-not-allowed disabled:opacity-50"
              >
                {uploadAttachment.isPending ? 'Uploading...' : 'Upload attachment'}
              </button>
            </form>
          </div>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-4">
          <div className="w-full max-w-2xl rounded-3xl bg-white p-6 shadow-soft">
            <div className="flex items-center justify-between border-b border-slate-100 pb-4">
              <div>
                <p className="section-title mb-1">Add patient</p>
                <h3 className="text-lg font-semibold text-slate-900">
                {selectedPatient?.name ?? "No patient selected"} · diagnostics
              </h3>
              </div>
              <button
                type="button"
                className="text-sm font-semibold text-slate-400 hover:text-slate-900"
                onClick={() => setShowModal(false)}
              >
                Close
              </button>
            </div>
            <div className="pt-4">
              {formError && (
                <div className="mb-4 rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm text-rose-600">
                  {formError}
                </div>
              )}
              <PatientForm onSubmit={handleCreatePatient} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Patients;
